import Page from "../../layout/Page";
import CockpitLayoutComponents from "../../components/CockpitLayoutComponents";
import COLORS from "../../config/colors";
import { storiesBaseUrl } from "../../config/urls";
import { useStories } from "../../hooks/CmsHooks";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { sortBy, uniq } from "lodash";
import Herobox from "../../components/Herobox";
import ContentSection from "../../components/ContentSection";
import { AnimatePresence, motion } from "motion/react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import "./style.scss";
import useInterval from "use-interval";
import { useEffect, useMemo, useState } from "react";
import { unwrapAsset } from "../../utils/CockpitUtils";
import BjelinButton from "../../components/BjelinButton/index.jsx";
import { useNavigationContext } from "../../functional-components/NavigationContext";

const StoriesPageHero = ({
  curType,
  filteredTypes,
  setFilteredTypes,
  filteredCategories,
  setFilteredCategories,
  filteredSubjects,
  setFilteredSubjects
}) => {
  const stories = useStories(curType);
  const types = sortBy(
    stories.reduce((res, story) => {
      return story.type ? uniq(res.concat(story.type)) : res;
    }, []),
    type => type
  );
  const filteredStories =
    filteredTypes.length === 0
      ? stories
      : stories.filter(story => {
          return story.type.some(type => filteredTypes.includes(type));
        });
  const subjects = sortBy(
    filteredStories.reduce((res, story) => {
      return story.subject ? uniq(res.concat(story.subject)) : res;
    }, []),
    subject => subject
  );
  const categories = sortBy(
    filteredStories.reduce((res, story) => {
      return story.category ? uniq(res.concat(story.category)) : res;
    }, []),
    category => category
  );

  const t = useBjelinTranslations();

  const slideShowStories = stories
    .filter(story => !!story?.featured_image)
    .slice(0, 5);

  const autoPlayInterval = 6000;
  const [curSlideIndex, setCurSlideIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  useInterval(
    () => {
      setCurSlideIndex(i => (i + 1) % slideShowStories.length);
    },
    !paused ? autoPlayInterval : false
  );

  const breadcrumbItems = [{ url: storiesBaseUrl, text: t("stories.heading") }];

  const curSlideShowStory = slideShowStories?.[curSlideIndex];

  return (
    <>
      <Herobox
        type={"fat"}
        textShadow={true}
        className={"text-center"}
        backgroundImage={
          curSlideShowStory?.featured_image &&
          unwrapAsset(curSlideShowStory?.featured_image)
        }
      >
        <AnimatePresence>
          <motion.div
            key={curType?.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {curType && <Breadcrumbs items={breadcrumbItems} />}
            <h1>
              {t("stories.heading")}{" "}
              {curType && <span>– {t("story-type." + curType)}</span>}
            </h1>
            <p>{t("stories.tagline")}</p>
          </motion.div>
        </AnimatePresence>
        <AnimatePresence>
          <motion.div
            key={curSlideIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1.5, delay: 0.5 } }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
          >
            <div className="product_link">
              <Link
                to={storiesBaseUrl + "/" + curSlideShowStory?.path}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
              >
                {t("read.story")}: <b>{curSlideShowStory?.title}</b>
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </Herobox>
      <ContentSection ariaLabel={t("stories.filter-heading")}>
        {!curType && (
          <>
            <div className="filter-heading">
              <h5>{t("stories.filter-heading")}</h5>
            </div>

            <div className="tags">
              <div className="filter-label">
                <span>
                  <span>{t("stories.filter.subject")}</span>
                </span>
              </div>
              <div className="filter-values">
                <AnimatePresence initial={false}>
                  {subjects.map(subject => (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      key={subject}
                      layout={true}
                    >
                      <BjelinButton
                        color={
                          filteredSubjects.includes(subject)
                            ? "bjelin-orange"
                            : "grey"
                        }
                        outline={!filteredSubjects.includes(subject)}
                        key={subject}
                        lg={false}
                        onClick={() =>
                          setFilteredSubjects(
                            filteredSubjects.includes(subject)
                              ? filteredSubjects.filter(t => t !== subject)
                              : [...filteredSubjects, subject]
                          )
                        }
                      >
                        {t("story.subject." + subject)}
                      </BjelinButton>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              <div className="tags">
                <div className="filter-label">
                  <span>
                    <span>{t("stories.filter.category")}</span>
                  </span>
                </div>
              </div>
              <div className="filter-values">
                <AnimatePresence initial={false}>
                  {categories.map(category => (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      key={category}
                      layout={true}
                    >
                      <BjelinButton
                        color={
                          filteredCategories.includes(category)
                            ? "bjelin-orange"
                            : "grey"
                        }
                        outline={!filteredCategories.includes(category)}
                        key={category}
                        lg={false}
                        onClick={() =>
                          setFilteredCategories(
                            filteredCategories.includes(category)
                              ? filteredCategories.filter(t => t !== category)
                              : [...filteredCategories, category]
                          )
                        }
                      >
                        {t("story.category." + category)}
                      </BjelinButton>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              {/*
              <div className="tags">
                <div className="filter-label">
                  <span>
                    <span>{t("stories.filter.type")}</span>
                  </span>
                </div>
                <div className="filter-values">
                  {types.map(type => (
                    <BjelinButton
                      color={
                        filteredTypes.includes(type) ? "bjelin-orange" : "grey"
                      }
                      outline={!filteredTypes.includes(type)}
                      key={type}
                      lg={false}
                      onClick={() =>
                        setFilteredTypes(
                          filteredTypes.includes(type)
                            ? filteredTypes.filter(t => t !== type)
                            : [...filteredTypes, type]
                        )
                      }
                    >
                      {typeIcons[type] && (
                        <>
                          <Icon name={typeIcons[type]} />{" "}
                        </>
                      )}
                      {t("story-type." + type)}
                    </BjelinButton>
                  ))}
                </div>
              </div>*/}
            </div>
          </>
        )}
      </ContentSection>
    </>
  );
};

const StoriesPage = ({ page }) => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filteredTypes = useMemo(
    () =>
      searchParams
        .get("types")
        ?.split("_")
        .filter(value => value !== "") || [],
    [searchParams]
  );
  const filteredCategories = useMemo(
    () =>
      searchParams
        .get("categories")
        ?.split("_")
        .filter(value => value !== "") || [],
    [searchParams]
  );
  const filteredSubjects = useMemo(
    () =>
      searchParams
        .get("subjects")
        ?.split("_")
        .filter(value => value !== "") || [],
    [searchParams]
  );

  const setFilter = (filter, values) => {
    const params = {};
    searchParams.forEach((value, key) => {
      if (key !== filter) {
        params[key] = value;
      }
    });
    if (values.length > 0) {
      params[filter] = values.sort().join("_");
    }
    if (params && searchParams.get(filter) !== params[filter]) {
      setSearchParams(params);
    }
  };

  const setFilteredTypes = types => {
    setFilter("types", types);
  };

  const setFilteredCategories = categories => {
    setFilter("categories", categories);
  };

  const setFilteredSubjects = subjects => {
    setFilter("subjects", subjects);
  };

  console.log("filteredTypes", filteredTypes);

  const curType = params.type;

  const { registerCurrentPaths } = useNavigationContext();

  useEffect(() => {
    registerCurrentPaths({
      all: storiesBaseUrl
    });
  }, [registerCurrentPaths]);

  return (
    <Page
      name="stories-page"
      key="stories-page"
      title={page.title}
      style={{ background: COLORS.bjelin_sand }}
      scrollToTop="no"
    >
      <StoriesPageHero
        curType={curType}
        filteredTypes={filteredTypes}
        setFilteredTypes={setFilteredTypes}
        filteredCategories={filteredCategories}
        setFilteredCategories={setFilteredCategories}
        filteredSubjects={filteredSubjects}
        setFilteredSubjects={setFilteredSubjects}
      />
      <CockpitLayoutComponents
        components={[
          {
            component: "story_grid",
            settings: {
              types: filteredTypes,
              categories: filteredCategories,
              subjects: filteredSubjects,
              limit: 360
            }
          }
        ]}
      />
    </Page>
  );
};

export default StoriesPage;
